import { useQuery } from '@tanstack/react-query';
import { type ComponentProps, useCallback, useEffect, useState } from 'react';
import { usePreviousDistinct } from 'react-use';

import useAssets from '~hooks/useAssets';
import useDelayFn from '~hooks/useDelayFn';
import { queries } from '~modules/media-library/controller/api/Queries';
import { type AssetId } from '~modules/media-library/model/Asset';
import { ZoomModal } from '~modules/media-library/view/ZoomModal/ZoomModal';

type ZoomModalControllerProps = Resolve<
  {
    assetId: AssetId;
  } & Pick<ComponentProps<typeof ZoomModal>, 'onClose'>
>;

export function ZoomModalController({ assetId, onClose }: ZoomModalControllerProps) {
  const { getAssetById, setAsset } = useAssets();
  const [isOpen, setIsOpen] = useState(false);
  const delayedOnClose = useDelayFn(onClose, 500);

  // get asset from store
  const asset = getAssetById(assetId);
  const prevAsset = usePreviousDistinct(asset, (_, next) => !Boolean(next));

  // load details and update asset in asset store
  const { data: assetData } = useQuery({
    ...queries.mediaLibrary.details(assetId),
    enabled: Boolean(assetId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  useEffect(() => {
    if (assetData === undefined) return;
    setAsset(assetData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetData]);

  // open details modal when a asset is set
  useEffect(() => {
    setIsOpen(Boolean(asset));
  }, [asset]);

  // Handler: close dialog, but delay the closing to have smoother transitions
  const handleOnClose = useCallback(() => {
    setIsOpen(false);
    delayedOnClose();
  }, [delayedOnClose]);

  return <ZoomModal isOpen={isOpen} asset={asset || prevAsset} onClose={handleOnClose} />;
}
